/**
 * Copyright © Binary Anvil, Inc. All rights reserved.
 * See https://binaryanvil.com/software/license for license details.
 */
import React, {ReactNode} from "react";
import Header from "@/app/components/Header/Header";
import {Container} from "@mui/material";
import HeadBlock, {THeadBlockProps} from "@/app/components/layouts/HeadBlock";
import {useAppSelector} from "@/app/hooks/redux";
import {MainNavProvider, useMainNavContext} from "@/app/context/MainNavProvider";
import {getCookie} from "@/app/utils/cookie";
import {SHOW_HEADER_BANNER_COOKIE} from "@/app/constants";
import Footer from '@/app/components/Footer/Footer';

const LayoutSidebarContent = (props: {
    children:ReactNode,
    HeadBlockProps: THeadBlockProps,
    ContentWrapperComponent: any,
    sidebarContent:ReactNode | null,
    sidebarPosition:string,
    beforeContent?:ReactNode | null,
    afterContent?:ReactNode,
    mainContainerClassName?:string,
    contentWrapperComponentProps?:any,
}) => {
    const {
        children,
        HeadBlockProps,
        ContentWrapperComponent,
        sidebarContent,
        sidebarPosition,
        beforeContent,
        afterContent,
        mainContainerClassName,
        contentWrapperComponentProps
    } = props;
    const {isMenuOpen:isMenuOpenMobile} = useAppSelector(state => state.mobileMenuReducer);
    const {isMainNavOpen} = useMainNavContext();
    const rootWrapperClasses = `root-wrapper ${isMenuOpenMobile ? 'nav-open-mobile' : ''} ${getCookie({name: SHOW_HEADER_BANNER_COOKIE}) === undefined ? 'with-header-ad-banner' : ''} ${isMainNavOpen ? 'nav-open-desktop' : ''}`.trim();

    return (
        <div className={rootWrapperClasses}>
            <HeadBlock {...HeadBlockProps} />

            <Header/>

            {beforeContent && beforeContent}

            <Container
                className={`main-content sidebar ${mainContainerClassName ? mainContainerClassName : ''}`} component={"main"}>
                <ContentWrapperComponent {...contentWrapperComponentProps} >
                    {sidebarContent &&
                        <aside className={`sidebar sidebar-${sidebarPosition}`}>
                            {sidebarContent}
                        </aside>
                    }
                    <main className={"page-content"}>
                        {children}
                    </main>
                </ContentWrapperComponent>
            </Container>

            {afterContent && afterContent}

            <Footer/>
        </div>
    );
}

const LayoutSidebar = (props: {
    children:ReactNode,
    headAdditionalNodes?:ReactNode,
    sidebarContent:ReactNode,
    sidebarPosition:string,
    beforeContent?:ReactNode,
    afterContent?:ReactNode,
    mainContainerClassName?:string,
    headTitle:string,
    metaDescription?:string,
    metaKeyword?:string,
    robotsContent?:string,
    canonical?:string,
    ogImgUrl?:string,
    contentWrapperComponent?:any,
    contentWrapperComponentProps?:any,
}) => {
    const {
        children,
        headAdditionalNodes,
        sidebarContent,
        sidebarPosition,
        beforeContent,
        afterContent,
        mainContainerClassName,
        headTitle,
        metaDescription,
        robotsContent,
        metaKeyword,
        ogImgUrl,
        contentWrapperComponentProps
    } = props;
    const HeadBlockProps: THeadBlockProps = {
        headTitle
    }

    if (metaKeyword) {
        Object.assign(HeadBlockProps, {metaKeyword})
    }

    if (metaDescription) {
        Object.assign(HeadBlockProps, {metaDescription})
    }

    if (robotsContent) {
        Object.assign(HeadBlockProps, {robotsContent})
    }

    if (ogImgUrl) {
        Object.assign(HeadBlockProps, {ogImgUrl})
    }

    if (headAdditionalNodes) {
        Object.assign(HeadBlockProps, {headAdditionalNodes})
    }

    if(props.canonical) {
        Object.assign(HeadBlockProps, {canonical:props.canonical})
    }

    const ContentWrapperComponent = props.contentWrapperComponent ? props.contentWrapperComponent : React.Fragment;

    return (
        <MainNavProvider>
            <LayoutSidebarContent
                HeadBlockProps={HeadBlockProps}
                ContentWrapperComponent={ContentWrapperComponent}
                sidebarContent={sidebarContent}
                sidebarPosition={sidebarPosition}
                beforeContent={beforeContent}
                afterContent={afterContent}
                mainContainerClassName={mainContainerClassName}
                contentWrapperComponentProps={contentWrapperComponentProps}
            >
                {children}
            </LayoutSidebarContent>
        </MainNavProvider>
    )
}

export default LayoutSidebar;